'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Eye, EyeOff, Check, ChevronRight, Copy } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import AppImage from '@/components/ui/AppImage';

interface LoginForm {
  email: string;
  password: string;
  remember: boolean;
}

interface RegisterForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  whatsapp: string;
  password: string;
  confirmPassword: string;
  vehiclePlate: string;
  vehicleBrand: string;
  vehicleModel: string;
  vehicleColor: string;
  vehicleSize: string;
  terms: boolean;
}

const DEMO_CREDENTIALS = [
  { role: 'Admin', email: 'admin@dripndrycuracao.com', password: 'DrAdmin2026!', href: '/admin-dashboard' },
  { role: 'Employee', email: 'jayden@dripndrycuracao.com', password: 'Crew2026!', href: '/employee-portal' },
  { role: 'Customer', email: 'keisha.martina@email.com', password: 'Wash2026!', href: '/customer-dashboard' },
];

export default function AuthPage() {
  const [tab, setTab] = useState<'login' | 'register'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const loginForm = useForm<LoginForm>();
  const registerForm = useForm<RegisterForm>();

  const handleLogin = async (data: LoginForm) => {
    setIsLoading(true);
    // Backend integration point: POST /api/auth/login
    await new Promise((r) => setTimeout(r, 1200));
    const match = DEMO_CREDENTIALS.find(
      (c) => c.email === data.email && c.password === data.password
    );
    setIsLoading(false);
    if (match) {
      toast.success(`Welcome back! Redirecting to ${match.role} dashboard...`);
      setTimeout(() => { window.location.href = match.href; }, 1200);
    } else {
      toast.error('Invalid credentials — use the demo accounts below to sign in');
    }
  };

  const handleRegister = async (data: RegisterForm) => {
    if (data.password !== data.confirmPassword) {
      registerForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setIsLoading(true);
    // Backend integration point: POST /api/auth/register
    await new Promise((r) => setTimeout(r, 1500));
    setIsLoading(false);
    toast.success('Account created! Check your WhatsApp for confirmation.');
  };

  const autofill = (cred: typeof DEMO_CREDENTIALS[0]) => {
    loginForm.setValue('email', cred.email);
    loginForm.setValue('password', cred.password);
    toast.info(`${cred.role} credentials filled`);
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left: Brand panel */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-5/12 flex-col justify-between p-10 xl:p-14 relative overflow-hidden" style={{background: 'linear-gradient(160deg, #0f172a 0%, #0c4a6e 60%, #0ea5e9 100%)'}}>
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[80, 160, 240, 320].map((size, i) => (
            <div
              key={`auth-bubble-${size}`}
              className="absolute rounded-full border border-sky-400 animate-float"
              style={{ width: size, height: size, top: `${15 + i * 18}%`, right: `${5 + i * 3}%`, opacity: 0.06, animationDelay: `${i * 0.8}s` }}
            />
          ))}
        </div>
        <div className="relative">
          <Link href="/" className="flex items-center gap-3">
            <AppLogo size={40} />
            <div>
              <p className="font-extrabold text-xl text-white leading-tight">Drip N Dry</p>
              <p className="text-sky-400 text-sm font-medium">Curaçao</p>
            </div>
          </Link>
        </div>
        <div className="relative">
          <AppImage
            src="/assets/images/ChatGPT_Image_Apr_30_2026_02_39_31_PM-1777574509496.png"
            alt="Drip N Dry Curaçao premium car wash service — gleaming clean car with water droplets"
            width={480}
            height={320}
            className="rounded-3xl shadow-2xl object-cover w-full mb-8"
          />
          <h2 className="text-3xl font-extrabold text-white mb-3">Premium Car Wash</h2>
          <p className="text-white/70 text-base leading-relaxed">
            Book, track, and manage your car wash experience. Loyalty rewards, subscriptions, and your full vehicle history — all in one place.
          </p>
          <div className="flex gap-6 mt-6">
            {[{ n: '500+', l: 'Customers' }, { n: '4.9★', l: 'Rating' }, { n: '250', l: 'XCG/mo' }].map((s) => (
              <div key={`auth-stat-${s.l}`}>
                <p className="text-xl font-extrabold text-white">{s.n}</p>
                <p className="text-xs text-white/50">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
        <p className="relative text-white/30 text-xs">© 2026 Drip N Dry Curaçao</p>
      </div>

      {/* Right: Auth form */}
      <div className="flex-1 flex flex-col justify-center px-6 py-10 md:px-10 xl:px-16 bg-white overflow-y-auto">
        <div className="max-w-md w-full mx-auto">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <AppLogo size={32} />
            <span className="font-extrabold text-lg text-dark">Drip N Dry <span className="text-primary">Curaçao</span></span>
          </div>

          {/* Tabs */}
          <div className="flex bg-sky-50 rounded-2xl p-1 mb-8">
            {(['login', 'register'] as const).map((t) => (
              <button
                key={`auth-tab-${t}`}
                onClick={() => setTab(t)}
                className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 ${
                  tab === t ? 'bg-white text-dark shadow-sm' : 'text-muted-foreground hover:text-dark'
                }`}
              >
                {t === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          {/* LOGIN FORM */}
          {tab === 'login' && (
            <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-4">
              <div>
                <h1 className="text-2xl font-extrabold text-dark mb-1">Welcome back</h1>
                <p className="text-sm text-muted-foreground">Sign in to manage your bookings and rewards</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email Address</label>
                <input
                  type="email"
                  {...loginForm.register('email', { required: 'Email is required' })}
                  className="w-full px-4 py-3 rounded-xl border border-input bg-sky-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  placeholder="you@email.com"
                />
                {loginForm.formState.errors.email && (
                  <p className="text-xs text-red-500 mt-1">{loginForm.formState.errors.email.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...loginForm.register('password', { required: 'Password is required' })}
                    className="w-full px-4 py-3 pr-12 rounded-xl border border-input bg-sky-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-dark transition-colors"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className="text-xs text-red-500 mt-1">{loginForm.formState.errors.password.message}</p>
                )}
              </div>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" {...loginForm.register('remember')} className="rounded border-input accent-primary" />
                  <span className="text-sm text-slate-600">Remember me</span>
                </label>
                <button type="button" className="text-sm text-primary hover:underline font-semibold">
                  Forgot password?
                </button>
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full justify-center py-3.5 text-sm disabled:opacity-70 min-h-[48px]"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  <>Sign In <ChevronRight size={16} /></>
                )}
              </button>

              {/* Demo credentials */}
              <div className="border border-sky-200 rounded-2xl p-4 bg-sky-50/60">
                <p className="text-xs font-bold text-slate-600 mb-3 uppercase tracking-wide">Demo Accounts</p>
                <div className="space-y-2">
                  {DEMO_CREDENTIALS.map((cred) => (
                    <div key={`demo-${cred.role}`} className="flex items-center justify-between bg-white rounded-xl px-3 py-2 border border-sky-100">
                      <div className="flex-1 min-w-0">
                        <span className={`badge text-xs mr-2 ${
                          cred.role === 'Admin' ? 'badge-assigned' :
                          cred.role === 'Employee' ? 'badge-onway' : 'badge-completed'
                        }`}>{cred.role}</span>
                        <span className="text-xs text-muted-foreground truncate">{cred.email}</span>
                      </div>
                      <div className="flex items-center gap-1 ml-2">
                        <button
                          type="button"
                          onClick={() => copyToClipboard(cred.password, `${cred.role} password`)}
                          className="p-1.5 rounded-lg hover:bg-sky-100 text-muted-foreground hover:text-primary transition-colors"
                          title="Copy password"
                        >
                          <Copy size={12} />
                        </button>
                        <button
                          type="button"
                          onClick={() => autofill(cred)}
                          className="px-2.5 py-1 rounded-lg bg-primary text-white text-xs font-semibold hover:bg-secondary transition-colors"
                        >
                          Fill
                        </button>
                        <Link
                          href={cred.href}
                          className="px-2.5 py-1 rounded-lg bg-sky-100 text-primary text-xs font-semibold hover:bg-sky-200 transition-colors"
                        >
                          Go →
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <p className="text-center text-sm text-muted-foreground">
                No account yet?{' '}
                <button type="button" onClick={() => setTab('register')} className="text-primary font-semibold hover:underline">
                  Create one free
                </button>
              </p>
            </form>
          )}

          {/* REGISTER FORM */}
          {tab === 'register' && (
            <form onSubmit={registerForm.handleSubmit(handleRegister)} className="space-y-4">
              <div>
                <h1 className="text-2xl font-extrabold text-dark mb-1">Create your account</h1>
                <p className="text-sm text-muted-foreground">Book washes, earn rewards, and track your vehicles</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">First Name *</label>
                  <input
                    {...registerForm.register('firstName', { required: 'Required' })}
                    className="w-full px-4 py-3 rounded-xl border border-input bg-sky-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    placeholder="Keisha"
                  />
                  {registerForm.formState.errors.firstName && (
                    <p className="text-xs text-red-500 mt-1">{registerForm.formState.errors.firstName.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Last Name *</label>
                  <input
                    {...registerForm.register('lastName', { required: 'Required' })}
                    className="w-full px-4 py-3 rounded-xl border border-input bg-sky-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    placeholder="Martina"
                  />
                  {registerForm.formState.errors.lastName && (
                    <p className="text-xs text-red-500 mt-1">{registerForm.formState.errors.lastName.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email Address *</label>
                <input
                  type="email"
                  {...registerForm.register('email', { required: 'Email is required', pattern: { value: /^\S+@\S+\.\S+$/, message: 'Invalid email' } })}
                  className="w-full px-4 py-3 rounded-xl border border-input bg-sky-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  placeholder="keisha@email.com"
                />
                {registerForm.formState.errors.email && (
                  <p className="text-xs text-red-500 mt-1">{registerForm.formState.errors.email.message}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Phone *</label>
                  <input
                    {...registerForm.register('phone', { required: 'Required' })}
                    className="w-full px-4 py-3 rounded-xl border border-input bg-sky-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    placeholder="+599 9 000 0000"
                  />
                  {registerForm.formState.errors.phone && (
                    <p className="text-xs text-red-500 mt-1">{registerForm.formState.errors.phone.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">WhatsApp</label>
                  <input
                    {...registerForm.register('whatsapp')}
                    className="w-full px-4 py-3 rounded-xl border border-input bg-sky-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    placeholder="Same as phone?"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Password *</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...registerForm.register('password', { required: 'Password is required', minLength: { value: 8, message: 'Min 8 characters' } })}
                    className="w-full px-4 py-3 pr-12 rounded-xl border border-input bg-sky-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    placeholder="Min 8 characters"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-dark">
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {registerForm.formState.errors.password && (
                  <p className="text-xs text-red-500 mt-1">{registerForm.formState.errors.password.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Confirm Password *</label>
                <div className="relative">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    {...registerForm.register('confirmPassword', { required: 'Please confirm password' })}
                    className="w-full px-4 py-3 pr-12 rounded-xl border border-input bg-sky-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    placeholder="Repeat password"
                  />
                  <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-dark">
                    {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {registerForm.formState.errors.confirmPassword && (
                  <p className="text-xs text-red-500 mt-1">{registerForm.formState.errors.confirmPassword.message}</p>
                )}
              </div>

              {/* Vehicle section */}
              <div className="border-t border-sky-100 pt-4">
                <p className="text-sm font-bold text-dark mb-3">Vehicle Information <span className="text-muted-foreground font-normal">(optional)</span></p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">License Plate</label>
                    <input
                      {...registerForm.register('vehiclePlate')}
                      className="w-full px-3 py-2.5 rounded-xl border border-input bg-sky-50/50 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-primary/30"
                      placeholder="ABC-1234"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Brand</label>
                    <input
                      {...registerForm.register('vehicleBrand')}
                      className="w-full px-3 py-2.5 rounded-xl border border-input bg-sky-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                      placeholder="Toyota"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Model</label>
                    <input
                      {...registerForm.register('vehicleModel')}
                      className="w-full px-3 py-2.5 rounded-xl border border-input bg-sky-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                      placeholder="Corolla"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Color</label>
                    <input
                      {...registerForm.register('vehicleColor')}
                      className="w-full px-3 py-2.5 rounded-xl border border-input bg-sky-50/50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                      placeholder="Silver"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" {...registerForm.register('terms', { required: 'You must accept terms' })} className="mt-0.5 accent-primary" />
                  <span className="text-xs text-muted-foreground leading-relaxed">
                    I agree to the{' '}
                    <Link href="/terms-and-conditions" className="text-primary hover:underline">Terms & Conditions</Link>,{' '}
                    <Link href="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>, and{' '}
                    <Link href="/privacy-policy" className="text-primary hover:underline">Photo Permission Policy</Link>
                  </span>
                </label>
                {registerForm.formState.errors.terms && (
                  <p className="text-xs text-red-500 mt-1">{registerForm.formState.errors.terms.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full justify-center py-3.5 text-sm disabled:opacity-70 min-h-[48px]"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Creating account...
                  </span>
                ) : (
                  <>Create Account <Check size={16} /></>
                )}
              </button>

              <p className="text-center text-sm text-muted-foreground">
                Already have an account?{' '}
                <button type="button" onClick={() => setTab('login')} className="text-primary font-semibold hover:underline">
                  Sign in
                </button>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}